<template>
    <div class="inbox">
        <div class="inbox-card">
            <div class="inbox-card-header">
                <div class="inbox-heading">
                    <h1>{{ t("inbox-page_title") }}</h1>
                    <span class="inbox-counter" v-if="total > 0">
                        {{ t("inbox-messages_counter", [messages.length, total]) }}
                    </span>
                </div>
                <kv-button
                    v-if="unreadInList > 0"
                    variant="tonal"
                    size="small"
                    prepend-icon="ui-check-done-02"
                    icon-size="2"
                    @click="markAllAsRead"
                >
                    {{ t("inbox-mark_all_read") }}
                </kv-button>
            </div>

            <div class="inbox-card-filters" v-if="filtersReady">
                <kv-filter
                    ref="filterBar"
                    :has-period-filter="false"
                    :has-branch-filter="canFilterPerBranch"
                    :has-client-filter="true"
                    :has-form-filter="true"
                    :filters="extraFilters"
                    @changed="handleChangedFilters"
                />
            </div>

            <div class="inbox-card-body">
                <loader v-if="status === 'loading' && (!messages || messages.length === 0)"></loader>

                <div class="inbox-empty" v-else-if="!messages || messages.length === 0">
                    <Icon icon-class="ui-mail-01" size="8" color="#D2D6DB" />
                    <p>{{ t("inbox-empty_state") }}</p>
                </div>

                <div class="message-list" v-else>
                    <div
                        class="message"
                        v-for="message in messages"
                        :key="message.id"
                        :class="getAnswerClasses(message)"
                        @click="openMessage(message)"
                    >
                        <span class="unread-dot" v-if="!message.read_at"></span>
                        <span class="name">{{ getDisplayName(message) }}</span>
                        <div class="meta-cell">
                            <span class="form-name">{{ message.form_name }}</span>
                            <div
                                class="type-summary"
                                v-if="visibleTypeSummary(message).length"
                            >
                                <kv-tooltip
                                    v-for="(item, index) in visibleTypeSummary(message)"
                                    :key="`${item.type}-${index}`"
                                    :text="typeSummaryTitle(item)"
                                    placement="top"
                                >
                                    <span
                                        class="type-item"
                                        :class="typeToneClass(item)"
                                    >
                                        <span :class="getIconForSummary(item)"></span>
                                    </span>
                                </kv-tooltip>
                            </div>
                            <answer-tags
                                compact
                                :tags="message.tags || []"
                                :suggestions="suggestionsFor(message)"
                                :busy="tagBusyId === message.id"
                                @apply="applyListTag(message, $event)"
                                @remove="removeListTag(message, $event)"
                                @update="
                                    updateTagDefinition(message, $event)
                                "
                                @delete="
                                    deleteTagDefinition(message, $event)
                                "
                            />
                        </div>
                        <human-date
                            class="date"
                            :date="message.created"
                            :format="t('dates-format_date_time')"
                        />
                        <button
                            type="button"
                            class="read-toggle"
                            :title="
                                message.read_at
                                    ? t('inbox-message-mark_unread')
                                    : t('inbox-mark_read')
                            "
                            @click.stop="toggleRead(message)"
                        >
                            <Icon
                                :icon-class="
                                    message.read_at ? 'ui-mail-01' : 'ui-mail-02'
                                "
                                size="3"
                            />
                        </button>
                    </div>
                </div>
            </div>

            <div class="inbox-card-footer" v-if="total > 0">
                <kv-pagination
                    :total="total"
                    :current-page="currentPage"
                    :items-per-page="perPage"
                    :show-items-per-page="true"
                    @page-change="goToPage"
                    @items-per-page-change="changePerPage"
                />
            </div>
        </div>

        <drawer
            ref="messageDrawer"
            id="inbox-message-drawer"
            size="xl"
            :has-header="false"
            :backdrop="false"
            @hide="handleDrawerHide"
        >
            <template #body="{ close }">
                <message
                    :id="activeMessage"
                    :place-tags="placeTags"
                    @close="close"
                    @tags-changed="onDrawerTagsChanged"
                    @duplicated="onAnswerDuplicated"
                />
            </template>
        </drawer>
    </div>
</template>
<script>
import FilterBar from "../components/reports/kv-filter-bar.vue";
import { mapState, mapActions, mapMutations } from "vuex";
import HumanDate from "../components/human-date/human-date";
import Message from "./_message";
import AnswerTags from "./answer-tags.vue";
import Loader from "../components/loader/loader";
import Drawer from "../components/drawer/drawer.vue";
import Icon from "../components/icon/icon";
import KvButton from "../components/form/kv-button.vue";
import KvPagination from "../components/ui/kv-pagination.vue";
import KvTooltip from "../components/ui/kv-tooltip.vue";
import keviApi from "../keviApi.js";

require("./inbox.store.js");

export default {
    components: {
        HumanDate,
        "kv-filter": FilterBar,
        Message,
        AnswerTags,
        Loader,
        Drawer,
        Icon,
        KvButton,
        KvPagination,
        KvTooltip,
    },
    data: function () {
        return {
            extraFilters: [],
            filtersReady: false,
            activeMessage: null,
            ready: false,
            filters: {},
            pendingFilters: {},
            placeTags: [],
            tagBusyId: null,
            localPerPage: 50,
        };
    },
    async mounted() {
        this.resetData();
        this.$store.dispatch("inbox/checkNewMessages");

        document.getElementsByTagName("title")[0].innerText = this.t(
            "inbox-browser_title",
        );

        this.extraFilters.push({
            name: "period",
            component: "period",
            label: this.t("inbox-filters-period"),
            removable: true,
        });

        this.extraFilters.push({
            component: "checkbox",
            name: "read_state",
            label: this.t("inbox-filters-read_state"),
            options: {
                options: [
                    { value: "unread", label: this.t("inbox-filters-unread") },
                    { value: "read", label: this.t("inbox-filters-read") },
                ],
                icon: "ui-mail-01",
            },
            removable: true,
        });

        // Opening an answer straight from a link (e.g. the client timeline)
        // must not wait for the list filters: the drawer loads the answer on
        // its own request.
        if (this.$route.query.message_id) {
            this.activeMessage = this.$route.query.message_id;
            this.markRead(this.activeMessage).catch(() => {});
            this.$nextTick(() => {
                if (this.$refs.messageDrawer) {
                    this.$refs.messageDrawer.open();
                }
            });
        }

        // The filter sources are independent: fetch them in parallel and
        // push them in the usual order (tags, segments, automations).
        const [, segments, automations] = await Promise.all([
            this.loadTagFilter(),
            this.$module("segments")
                ? this.$http(keviApi.getOptions("segments"))
                      .then((response) => response.body || [])
                      .catch(() => null)
                : Promise.resolve(null),
            this.$module("automations")
                ? this.$http(keviApi.getOptions("inbox/automations"))
                      .then((response) => response.body || [])
                      .catch(() => null)
                : Promise.resolve(null),
        ]);

        if (segments) {
            this.extraFilters.push({
                component: "checkbox",
                label: this.t("inbox-filters-client_segments"),
                name: "client-segments",
                options: {
                    options: segments.map((seg) => ({
                        value: seg.id,
                        label: seg.name,
                    })),
                    icon: "ui-users-01",
                },
                removable: true,
            });
        }

        if (automations) {
            this.extraFilters.push({
                component: "checkbox",
                label: this.t("inbox-filters-automations"),
                name: "automations",
                options: {
                    options: automations.map((auto) => ({
                        value: auto.id,
                        label: auto.title,
                    })),
                    icon: "ui-refresh-ccw-01",
                },
                removable: true,
            });
        }

        this.filtersReady = true;

        this.$nextTick(() => {
            this.ready = true;
            this.scheduleReload(this.$refs.filterBar ? this.$refs.filterBar.values : {});
        });
    },
    computed: {
        ...mapState({
            messages: (state) => state.inbox.list,
            status: (state) => state.inbox.status,
            user: (state) => state.user,
            total: (state) => state.inbox.totalMessages,
            currentPage: (state) => state.inbox.currentPage,
            storePerPage: (state) => state.inbox.perPage,
        }),
        perPage() {
            return this.localPerPage || this.storePerPage || 50;
        },
        unreadInList() {
            if (!this.messages) return 0;
            return this.messages.filter((message) => !message.read_at).length;
        },
        canFilterPerBranch() {
            let inbox = this.user.permissions.inbox;
            return (
                parseInt(inbox.enabled) === 1 &&
                parseInt(inbox.options.global_access) === 1
            );
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        ...mapActions({
            getMessages: "inbox/loadList",
            markRead: "inbox/markRead",
            markUnread: "inbox/markUnread",
            markAllRead: "inbox/markAllRead",
            applyTagAction: "inbox/applyTag",
            removeTagAction: "inbox/removeTag",
        }),

        ...mapMutations({
            resetStoreData: "inbox/resetData",
            setMessageRead: "inbox/setMessageRead",
        }),

        async loadTagFilter() {
            try {
                const response = await this.$http(keviApi.getOptions("forms-tags"));
                const tags = (response.body && response.body.tags) || [];
                this.placeTags = tags;
                if (!tags.length) {
                    return;
                }
                this.extraFilters.push({
                    component: "checkbox",
                    name: "tags",
                    label: this.t("inbox-filters-tags"),
                    options: {
                        options: tags.map((tag) => ({
                            value: tag.id,
                            label: tag.name,
                            color: tag.color,
                        })),
                        icon: "ui-tag-01",
                    },
                    removable: true,
                });
            } catch (error) {
                console.error("Error loading tags filter", error);
            }
        },

        suggestionsFor() {
            return this.placeTags || [];
        },

        rememberCreatedTag(tag) {
            if (!tag || !tag.id) return;
            const exists = this.placeTags.some(
                (item) => String(item.id) === String(tag.id),
            );
            if (!exists) {
                this.placeTags = this.placeTags.concat([tag]);
            }
        },

        resetData() {
            this.resetStoreData();
        },

        loadPage(page = 1) {
            const filters = {
                ...this.filters,
                page,
                per_page: this.perPage,
            };
            return this.getMessages(filters);
        },

        handleChangedFilters(filters) {
            this.scheduleReload(filters);
        },

        // O filtro emite "changed" na montagem quando ha filtros na URL; o debounce
        // evita que a carga inicial e a do filtro disparem duas requisicoes.
        scheduleReload(filters) {
            if (!this.ready) return;
            this.pendingFilters = filters || {};
            this.debouncedReload();
        },

        debouncedReload: debounce(function () {
            this.filters = this.pendingFilters || {};
            this.loadPage(1);
        }, 60),

        goToPage(page) {
            this.loadPage(page);
        },

        changePerPage(value) {
            this.localPerPage = value;
            this.loadPage(1);
        },

        getAnswerClasses(message) {
            return {
                unread: !message.read_at,
                selected: String(message.id) === String(this.activeMessage),
            };
        },
        isTypingTarget(target) {
            if (!target) return false;
            const tag = (target.tagName || "").toLowerCase();
            if (["input", "textarea", "select"].includes(tag)) return true;
            return !!target.isContentEditable;
        },
        handleInboxKeydown(event) {
            if (!this.activeMessage || !this.$refs.messageDrawer?.isOpen) {
                return;
            }
            if (this.isTypingTarget(event.target)) {
                return;
            }
            if (event.key === "ArrowDown") {
                event.preventDefault();
                this.navigateMessage(1);
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                this.navigateMessage(-1);
            }
        },
        navigateMessage(delta) {
            if (!this.messages || !this.messages.length) return;
            const index = this.messages.findIndex(
                (message) => String(message.id) === String(this.activeMessage),
            );
            const nextIndex = index + delta;
            if (nextIndex < 0 || nextIndex >= this.messages.length) {
                return;
            }
            this.openMessage(this.messages[nextIndex]);
            this.$nextTick(() => {
                const selected = this.$el.querySelector(".message.selected");
                if (selected && selected.scrollIntoView) {
                    selected.scrollIntoView({ block: "nearest" });
                }
            });
        },
        getDisplayName(message) {
            if (message.first_name && message.first_name.length > 0) {
                return message.first_name;
            }
            return message.whatsapp;
        },
        getIconForSummary(item) {
            if (item.type === "emotion") {
                return this.getEmotionIcon(item.value);
            }
            const icons = {
                comment: "mdi mdi-comment-text-outline type-icon",
                boolean: "mdi mdi-check-circle-outline type-icon",
                selectbox: "mdi mdi-format-list-bulleted type-icon",
                input: "mdi mdi-format-text type-icon",
                nps: "mdi mdi-gauge type-icon",
                ces: "mdi mdi-arm-flex-outline type-icon",
                date: "mdi mdi-calendar-outline type-icon",
                file: "mdi mdi-paperclip type-icon",
            };
            return icons[item.type] || "mdi mdi-circle-small type-icon";
        },
        getEmotionIcon(value) {
            const score = Math.round(parseFloat(value) || 0);
            if (score >= 5) {
                return "mdi mdi-emoticon-excited-outline type-icon";
            }
            if (score >= 4) {
                return "mdi mdi-emoticon-happy-outline type-icon";
            }
            if (score >= 3) {
                return "mdi mdi-emoticon-neutral-outline type-icon";
            }
            if (score >= 2) {
                return "mdi mdi-emoticon-sad-outline type-icon";
            }
            return "mdi mdi-emoticon-cry-outline type-icon";
        },
        visibleTypeSummary(message) {
            return (message.type_summary || []).filter((item) => {
                // Comment/file/date: icon only when there is at least one answer.
                if (item.count != null) {
                    return parseInt(item.count, 10) > 0;
                }
                return item.value != null;
            });
        },
        formatTypeValue(item) {
            if (item.type === "boolean") {
                return parseInt(item.value, 10) > 0
                    ? this.t("inbox-message-bool_yes")
                    : this.t("inbox-message-bool_no");
            }
            return item.value;
        },
        typeToneClass(item) {
            return `tone-${this.typeTone(item)}`;
        },
        typeTone(item) {
            if (item.value == null) {
                return "muted";
            }
            const value = parseFloat(item.value);
            if (item.type === "nps") {
                if (value > 8) return "good";
                if (value > 6) return "neutral";
                return "bad";
            }
            if (item.type === "ces") {
                if (value > 4) return "good";
                if (value === 4) return "neutral";
                return "bad";
            }
            if (item.type === "emotion") {
                if (value >= 4) return "good";
                if (value >= 3) return "neutral";
                return "bad";
            }
            if (item.type === "boolean") {
                return value > 0 ? "good" : "bad";
            }
            return "muted";
        },
        typeSummaryTitle(item) {
            const typeLabel = this.t("inbox-type_" + item.type);
            if (item.type === "nps" && item.value != null) {
                const value = parseFloat(item.value);
                const band =
                    value > 8
                        ? this.t("inbox-message-nps_promoter")
                        : value > 6
                          ? this.t("inbox-message-nps_neutral")
                          : this.t("inbox-message-nps_detractor");
                return `${typeLabel}: ${item.value} (${band})`;
            }
            if (item.type === "ces" && item.value != null) {
                const value = parseFloat(item.value);
                const band =
                    value > 4
                        ? this.t("inbox-message-ces_easy")
                        : value === 4
                          ? this.t("inbox-message-ces_medium")
                          : this.t("inbox-message-ces_hard");
                return `${typeLabel}: ${item.value} (${band})`;
            }
            if (item.value != null) {
                return `${typeLabel}: ${this.formatTypeValue(item)}`;
            }
            if (item.count) {
                return `${typeLabel}: ${item.count}`;
            }
            return typeLabel;
        },
        toggleRead(message) {
            const action = message.read_at
                ? this.markUnread(message.id)
                : this.markRead(message.id);
            action.catch(() => {
                this.$store.dispatch(
                    "notification/error",
                    this.t("inbox-notification_read_failed"),
                );
            });
        },
        markAllAsRead() {
            this.markAllRead()
                .then(() => {
                    this.$store.dispatch(
                        "notification/success",
                        this.t("inbox-notification_all_read"),
                    );
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-notification_read_failed"),
                    );
                });
        },
        applyListTag(message, payload) {
            this.tagBusyId = message.id;
            this.applyTagAction({
                messageId: message.id,
                ...payload,
            })
                .then((body) => {
                    if (body && body.tag) {
                        this.rememberCreatedTag(body.tag);
                    }
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusyId = null;
                });
        },
        removeListTag(message, tag) {
            this.tagBusyId = message.id;
            this.removeTagAction({
                messageId: message.id,
                tagId: tag.id,
            })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusyId = null;
                });
        },
        patchTagInCatalog(tag) {
            if (!tag || !tag.id) return;
            this.placeTags = (this.placeTags || []).map((item) =>
                String(item.id) === String(tag.id)
                    ? { ...item, ...tag }
                    : item,
            );
            (this.messages || []).forEach((message) => {
                const tags = message.tags || [];
                if (!tags.some((item) => String(item.id) === String(tag.id))) {
                    return;
                }
                this.$store.commit("inbox/setMessageTags", {
                    messageId: message.id,
                    tags: tags.map((item) =>
                        String(item.id) === String(tag.id)
                            ? { ...item, ...tag }
                            : item,
                    ),
                });
            });
        },
        removeTagFromCatalog(tagId) {
            this.placeTags = (this.placeTags || []).filter(
                (item) => String(item.id) !== String(tagId),
            );
            (this.messages || []).forEach((message) => {
                const tags = message.tags || [];
                if (!tags.some((item) => String(item.id) === String(tagId))) {
                    return;
                }
                this.$store.commit("inbox/setMessageTags", {
                    messageId: message.id,
                    tags: tags.filter(
                        (item) => String(item.id) !== String(tagId),
                    ),
                });
            });
        },
        updateTagDefinition(message, payload) {
            if (!payload || !payload.id || !message) {
                return;
            }
            this.tagBusyId = message.id;
            const options = keviApi.getOptions("forms-tags", "POST", {
                id: payload.id,
                name: payload.name,
                color: payload.color,
            });
            this.$http(options)
                .then((response) => {
                    const tag =
                        (response.body && response.body.tag) || payload;
                    this.patchTagInCatalog(tag);
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusyId = null;
                });
        },
        deleteTagDefinition(message, tag) {
            if (!tag || !tag.id || !message) {
                return;
            }
            this.tagBusyId = message.id;
            const options = keviApi.getOptions(
                "forms-tags/" + tag.id,
                "DELETE",
            );
            this.$http(options)
                .then(() => {
                    this.removeTagFromCatalog(tag.id);
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagBusyId = null;
                });
        },
        onDrawerTagsChanged({ tag, deletedTagId }) {
            if (deletedTagId) {
                this.removeTagFromCatalog(deletedTagId);
                return;
            }
            if (tag) {
                const exists = this.placeTags.some(
                    (item) => String(item.id) === String(tag.id),
                );
                if (exists) {
                    this.patchTagInCatalog(tag);
                } else {
                    this.rememberCreatedTag(tag);
                }
            }
        },
        onAnswerDuplicated(newId) {
            this.loadPage(this.currentPage).then(() => {
                if (!newId) return;
                const created = (this.messages || []).find(
                    (message) => String(message.id) === String(newId),
                );
                if (created) {
                    this.openMessage(created);
                } else {
                    this.activeMessage = newId;
                    this.$refs.messageDrawer.open();
                }
            });
        },
        openMessage(message) {
            this.activeMessage = message.id;
            if (!message.read_at) {
                this.markRead(message.id).catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-notification_read_failed"),
                    );
                });
            }
            this.$refs.messageDrawer.open();
        },
        handleDrawerHide() {
            this.activeMessage = null;
        },
    },
    created() {
        document.addEventListener("keydown", this.handleInboxKeydown);
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleInboxKeydown);
    },
    watch: {
        activeMessage() {
            let newQuery = JSON.parse(JSON.stringify(this.$route.query));
            if (this.activeMessage) {
                newQuery["message_id"] = this.activeMessage;
            } else {
                delete newQuery["message_id"];
            }
            this.$router.replace({ query: newQuery }).catch(() => {});
        },
    },
};


function debounce(fn, delay) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
}
</script>
